// The channel's uploads playlist id is "UU" plus the channel id without its "UC" prefix.
// YouTube's iframe API returns those video ids. oEmbed on the /shorts/ URL returns the title
// and a portrait size for Shorts, a landscape size for regular videos. Neither needs an API key.
// The player is removed as soon as the ids are read. Playback uses a separate youtube-nocookie embed.

const PLAYLIST_CACHE_MS = 60 * 60 * 1000;
const TITLE_CONCURRENCY = 8;
const CLASSIFY_BATCH = 24;

class AriYoutubeChannel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.body = this.querySelector('[data-yt-body]');
    this.status = this.querySelector('[data-yt-status]');
    this.dialog = this.querySelector('[data-yt-dialog]');
    this.dialogPanel = this.querySelector('[data-yt-dialog-panel]');
    this.dialogTitle = this.querySelector('[data-yt-dialog-title]');
    this.frame = this.querySelector('[data-yt-frame]');
    this.cardTemplate = this.querySelector('[data-yt-card]');
    this.shortTemplate = this.querySelector('[data-yt-short]');
    this.pageSize = Number(this.dataset.pageSize) || 6;
    this.channelUrl = this.dataset.channelUrl || '';
    this.playlistId = uploadsPlaylistId(this.channelUrl);
    this.ids = [];
    this.meta = new Map();
    this.filter = 'all';
    this.limit = this.pageSize;
    this.shortLimit = this.pageSize;
    this.generation = 0;
    this.ready = false;
    this.opener = null;

    this.addEventListener('click', (event) => {
      const readMore = event.target.closest('[data-yt-feature-more]');
      if (readMore && this.contains(readMore)) {
        this.toggleDescription(readMore);
        return;
      }

      const play = event.target.closest('[data-yt-play]');
      if (play && this.contains(play)) {
        const id = play.dataset.videoId;
        if (!id) return;
        const info = this.meta.get(id);
        const title = info?.title || play.querySelector('[data-yt-title], [data-yt-feature-title]')?.textContent || '';
        this.open(id, title, play);
        return;
      }

      const tab = event.target.closest('[data-yt-filter]');
      if (tab && this.contains(tab)) {
        this.setFilter(tab.dataset.ytFilter);
        return;
      }

      const see = event.target.closest('[data-yt-see]');
      if (see && this.contains(see)) {
        this.setFilter(see.dataset.ytSee);
        return;
      }

      if (event.target.closest('[data-yt-more]')) this.loadMore();
    });

    this.dialog?.addEventListener('close', () => {
      if (this.frame) this.frame.src = 'about:blank';
      this.opener?.focus();
      this.opener = null;
    });

    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.dialogPanel?.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    this.querySelector('[data-yt-close]')?.addEventListener('click', () => {
      this.dialog?.close();
    });

    this.load();
  }

  async load() {
    if (!this.playlistId || !this.body) {
      this.fail();
      return;
    }

    this.setStatus('Loading videos');

    try {
      const cached = this.readCache();
      if (cached) {
        this.ids = cached.ids;
        this.meta = new Map(Object.entries(cached.meta || {}));
      } else {
        this.ids = await this.fetchPlaylistIds();
        if (this.ids.length) this.writeCache();
      }

      if (!this.isConnected) return;
      if (!this.ids.length) throw new Error('empty');

      this.ready = true;
      await this.ensurePainted();
    } catch (error) {
      if (!this.isConnected) return;
      this.fail();
    }
  }

  setFilter(name) {
    if (name !== 'all' && name !== 'videos' && name !== 'shorts') return;
    if (name === this.filter) return;
    this.filter = name;
    this.limit = this.pageSize;
    this.shortLimit = this.pageSize;
    this.updateTabs();
    if (!this.ready) return;
    if (this.readyToPaint()) {
      this.render();
      return;
    }
    this.body.replaceChildren();
    this.ensurePainted();
  }

  loadMore() {
    if (this.filter === 'shorts') this.shortLimit += this.pageSize;
    else this.limit += this.pageSize;
    if (this.readyToPaint()) this.render();
    else this.ensurePainted();
  }

  async ensurePainted() {
    const generation = ++this.generation;
    if (!this.readyToPaint()) this.setStatus('Loading videos');
    const ready = await this.classifyUntil(generation, () => this.readyToPaint());
    if (!ready || generation !== this.generation || !this.isConnected) return;
    this.render();
    if (this.fullyClassified()) return;
    const rest = await this.classifyUntil(generation, () => this.fullyClassified());
    if (!rest || generation !== this.generation || !this.isConnected) return;
    this.render();
  }

  idsOf(wantShort) {
    return this.ids.filter((id) => {
      const info = this.meta.get(id);
      return info && info.short === wantShort && !info.failed;
    });
  }

  fullyClassified() {
    return this.ids.every((id) => {
      const info = this.meta.get(id);
      return info && typeof info.short === 'boolean';
    });
  }

  readyToPaint() {
    const videos = this.idsOf(false);
    const shorts = this.idsOf(true);
    const done = this.fullyClassified();
    if (this.filter === 'videos') return done || videos.length >= this.limit;
    if (this.filter === 'shorts') return done || shorts.length >= this.shortLimit;
    return done || videos.length >= this.pageSize + 1;
  }

  async classifyUntil(generation, predicate) {
    let index = 0;
    while (!predicate() && index < this.ids.length) {
      if (generation !== this.generation || !this.isConnected) return false;
      const batch = [];
      while (batch.length < CLASSIFY_BATCH && index < this.ids.length) {
        const id = this.ids[index];
        index += 1;
        const info = this.meta.get(id);
        if (!info || typeof info.short !== 'boolean') batch.push(id);
      }
      if (!batch.length) break;
      await this.fetchMeta(batch);
      if (generation !== this.generation || !this.isConnected) return false;
      this.writeCache();
    }
    return generation === this.generation;
  }

  render() {
    const videos = this.idsOf(false);
    const shorts = this.idsOf(true);
    const isAll = this.filter === 'all';
    this.updateTabs(videos.length, shorts.length);
    this.paintHero(videos[0]);

    const rail = this.body.querySelector('[data-yt-rail]');
    const railScroll = rail ? rail.scrollLeft : 0;
    this.body.replaceChildren();

    if (this.filter !== 'shorts') {
      const pool = isAll ? videos.slice(1) : videos;
      const shown = pool.slice(0, isAll ? this.pageSize : this.limit);
      if (shown.length) {
        const seeAll = isAll && pool.length > shown.length ? `See all ${videos.length}` : '';
        const section = this.buildSection('Videos', seeAll, 'videos');
        const grid = document.createElement('div');
        grid.className = 'ari-yt-grid';
        shown.forEach((id, index) => grid.append(this.createCard(this.cardTemplate, id, index)));
        section.append(grid);
        if (!isAll && (pool.length > shown.length || !this.fullyClassified())) {
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'ari-yt-more';
          more.dataset.ytMore = '';
          more.textContent = this.dataset.moreLabel || 'Load more videos';
          section.append(more);
        }
        this.body.append(section);
      } else if (this.fullyClassified() && this.filter === 'videos') {
        this.setStatus('No full-length videos in the latest uploads.');
      }
    }

    if (this.filter !== 'videos') {
      const shown = shorts.slice(0, isAll ? this.pageSize : this.shortLimit);
      if (shown.length) {
        const seeAll = isAll && shorts.length > shown.length ? `See all ${shorts.length}` : '';
        const section = this.buildSection('Shorts', seeAll, 'shorts');
        const wrap = document.createElement('div');
        wrap.className = isAll ? 'ari-yt-rail' : 'ari-yt-grid ari-yt-grid--shorts';
        if (isAll) wrap.dataset.ytRail = '';
        shown.forEach((id, index) => wrap.append(this.createCard(this.shortTemplate, id, index + 3)));
        section.append(wrap);
        if (!isAll && (shorts.length > shown.length || !this.fullyClassified())) {
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'ari-yt-more';
          more.dataset.ytMore = '';
          more.textContent = 'Load more shorts';
          section.append(more);
        }
        this.body.append(section);
      } else if (this.fullyClassified() && this.filter === 'shorts') {
        this.setStatus('No Shorts in the latest uploads.');
      }
    }

    if (this.body.childElementCount) this.setStatus('');
    const nextRail = this.body.querySelector('[data-yt-rail]');
    if (nextRail) nextRail.scrollLeft = railScroll;
  }

  buildSection(title, seeAll, filter) {
    const section = document.createElement('section');
    section.className = 'ari-yt-block';
    const head = document.createElement('div');
    head.className = 'ari-yt-block__head';
    const heading = document.createElement('h2');
    heading.className = 'ari-yt-block__title';
    heading.textContent = title;
    head.append(heading);
    if (seeAll) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ari-yt-seeall';
      button.dataset.ytSee = filter;
      button.textContent = seeAll;
      head.append(button);
    }
    section.append(head);
    return section;
  }

  paintHero(videoId) {
    const column = this.querySelector('[data-yt-feature-col]');
    const button = this.querySelector('[data-yt-feature]');
    if (!videoId || !column || !button) {
      if (column) column.hidden = true;
      this.querySelectorAll('[data-yt-skel]').forEach((node) => node.remove());
      return;
    }
    column.hidden = false;
    this.paintFeature(button, this.querySelector('[data-yt-feature-img]'), this.querySelector('[data-yt-feature-title]'), videoId);
    this.querySelectorAll('[data-yt-skel]').forEach((node) => node.remove());
    const known = this.meta.get(videoId);
    if (typeof known?.description === 'string') {
      known.description = tidyDescription(known.description);
      if (known.description) {
        this.paintDescription(known.description);
        return;
      }
    }
    if (!known?.descLoading) this.loadDescription(videoId);
  }

  paintDescription(text) {
    const node = this.querySelector('[data-yt-feature-desc]');
    const more = this.querySelector('[data-yt-feature-more]');
    if (!node) return;
    const clean = tidyDescription(text);
    node.classList.remove('is-open');
    node.textContent = clean;
    node.hidden = !clean;
    if (more) {
      more.hidden = true;
      more.textContent = 'Read more';
      more.setAttribute('aria-expanded', 'false');
    }
    if (!clean || !more) return;
    requestAnimationFrame(() => {
      if (!this.isConnected || node.hidden) return;
      more.hidden = node.scrollHeight <= node.clientHeight + 1;
    });
  }

  toggleDescription(button) {
    const node = this.querySelector('[data-yt-feature-desc]');
    if (!node) return;
    const open = !node.classList.contains('is-open');
    node.classList.toggle('is-open', open);
    button.textContent = open ? 'Show less' : 'Read more';
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  async loadDescription(id) {
    const info = this.meta.get(id);
    if (!info || (typeof info.description === 'string' && !/\bmore\s*$/i.test(info.description))) {
      this.paintDescription(info?.description || '');
      return;
    }
    info.descLoading = true;
    try {
      const response = await fetch(`https://r.jina.ai/https://www.youtube.com/watch?v=${encodeURIComponent(id)}`, {
        headers: { 'X-Target-Selector': 'ytd-text-inline-expander' },
      });
      if (!response.ok) throw new Error('description');
      const description = extractDescription(await response.text());
      info.description = description;
      this.writeCache();
      if (this.querySelector('[data-yt-feature]')?.dataset.videoId === id) this.paintDescription(description);
    } catch (error) {
      info.description = '';
      this.paintDescription('');
    }
  }

  paintFeature(button, image, titleEl, id) {
    if (!button || !image || !titleEl) return;
    if (!id) {
      button.hidden = true;
      delete button.dataset.videoId;
      return;
    }
    button.hidden = false;
    button.dataset.videoId = id;
    const title = this.meta.get(id)?.title || '';
    titleEl.textContent = title;
    button.setAttribute('aria-label', title ? `Play ${title}` : 'Play video');
    if (image.dataset.id !== id) {
      image.dataset.id = id;
      this.setThumb(image, id);
    }
  }

  createCard(template, id, index) {
    const fragment = template.content.cloneNode(true);
    const button = fragment.querySelector('[data-yt-play]');
    const image = fragment.querySelector('[data-yt-thumb]');
    const titleEl = fragment.querySelector('[data-yt-title]');
    const title = this.meta.get(id)?.title || '';
    button.dataset.videoId = id;
    if (title) titleEl.textContent = title;
    button.setAttribute('aria-label', title ? `Play ${title}` : 'Play video');
    if (index > 2) image.loading = 'lazy';
    this.setThumb(image, id);
    return fragment;
  }

  setThumb(image, id) {
    const fallback = `https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`;
    image.addEventListener('error', () => {
      if (image.src !== fallback) image.src = fallback;
    }, { once: true });
    image.src = `https://i.ytimg.com/vi/${encodeURIComponent(id)}/maxresdefault.jpg`;
  }

  updateTabs(videoCount = this.idsOf(false).length, shortCount = this.idsOf(true).length) {
    const counts = {
      all: videoCount + shortCount,
      videos: videoCount,
      shorts: shortCount,
    };
    this.querySelectorAll('[data-yt-filter]').forEach((tab) => {
      const selected = tab.dataset.ytFilter === this.filter;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
    this.querySelectorAll('[data-yt-count]').forEach((node) => {
      const count = counts[node.dataset.ytCount] || 0;
      node.textContent = count ? String(count) : '';
      node.hidden = count === 0;
    });
  }

  async fetchMeta(ids) {
    await mapPool(ids, TITLE_CONCURRENCY, async (id) => {
      try {
        const endpoint = `https://www.youtube.com/oembed?url=${encodeURIComponent(`https://www.youtube.com/shorts/${id}`)}&format=json`;
        const response = await fetch(endpoint);
        if (!response.ok) {
          this.meta.set(id, { title: '', short: false, failed: true });
          return;
        }
        const data = await response.json();
        const width = Number(data.width) || 0;
        const height = Number(data.height) || 0;
        this.meta.set(id, {
          title: data.title || '',
          short: height > width,
          failed: false,
        });
      } catch (error) {
        this.meta.set(id, { title: '', short: false, failed: true });
      }
    });
  }

  fetchPlaylistIds() {
    const playlistId = this.playlistId;
    return new Promise((resolve, reject) => {
      const cradle = document.createElement('div');
      cradle.inert = true;
      cradle.setAttribute('aria-hidden', 'true');
      cradle.style.cssText = 'position:absolute;width:200px;height:200px;overflow:hidden;clip-path:inset(50%);pointer-events:none;';
      const host = document.createElement('div');
      cradle.appendChild(host);
      this.appendChild(cradle);

      let settled = false;
      const timeout = window.setTimeout(() => {
        cradle.remove();
        reject(new Error('timeout'));
      }, 12000);

      const finish = (player, ids) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        try {
          player?.destroy();
        } catch (error) {
          // The cradle removal below still drops the iframe.
        }
        cradle.remove();
        resolve((ids || []).filter((id) => /^[\w-]{6,}$/.test(id)));
      };

      loadYouTubeApi()
        .then(() => {
          if (!this.isConnected) {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeout);
            cradle.remove();
            reject(new Error('removed'));
            return;
          }

          new window.YT.Player(host, {
            width: 200,
            height: 200,
            playerVars: {
              listType: 'playlist',
              list: playlistId,
              autoplay: 0,
              controls: 0,
              disablekb: 1,
              fs: 0,
              mute: 1,
              modestbranding: 1,
              rel: 0,
              playsinline: 1,
            },
            events: {
              onReady: (event) => {
                const read = () => event.target.getPlaylist() || [];
                const ids = read();
                if (ids.length) {
                  finish(event.target, ids);
                  return;
                }
                window.setTimeout(() => finish(event.target, read()), 600);
              },
              onError: () => {
                if (settled) return;
                settled = true;
                window.clearTimeout(timeout);
                cradle.remove();
                reject(new Error('player'));
              },
            },
          });
        })
        .catch((error) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          cradle.remove();
          reject(error);
        });
    });
  }

  open(id, title, opener) {
    if (!this.dialog || !this.frame || !/^[\w-]{6,}$/.test(id)) return;
    const short = Boolean(this.meta.get(id)?.short);
    this.opener = opener;
    this.dialog.classList.toggle('is-short', short);
    if (this.dialogTitle) this.dialogTitle.textContent = title || '';
    // A fresh iframe each time, so the player API attaches to this video rather than the last one.
    const frame = this.frame.cloneNode(false);
    frame.removeAttribute('src');
    frame.removeAttribute('id');
    this.frame.replaceWith(frame);
    this.frame = frame;
    frame.title = title ? `Play ${title}` : 'Play video';
    const src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1`;
    if (!this.dialog.open) this.dialog.showModal();
    import('@theme/ari-video-embed')
      .then(({ playEmbed }) => playEmbed(frame, src))
      .catch(() => {
        frame.src = src;
      });
  }

  fail() {
    this.querySelectorAll('[data-yt-skel]').forEach((node) => node.remove());
    const localIp = window.location.hostname === '127.0.0.1';
    const message = localIp
      ? 'YouTube blocks embeds on 127.0.0.1. Open this preview at localhost instead.'
      : this.dataset.error || 'Videos aren’t available right now.';
    this.setStatus(message);
  }

  setStatus(message) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = message === '';
  }

  cacheKey() {
    return `ari-yt:v3:${this.playlistId}`;
  }

  readCache() {
    try {
      const raw = window.localStorage.getItem(this.cacheKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.ids) || !parsed.ids.length || Date.now() - parsed.savedAt > PLAYLIST_CACHE_MS) return null;
      const meta = {};
      Object.entries(parsed.meta || {}).forEach(([id, value]) => {
        if (value && typeof value.short === 'boolean') meta[id] = value;
      });
      return { ids: parsed.ids, meta };
    } catch (error) {
      return null;
    }
  }

  writeCache() {
    try {
      const meta = {};
      this.meta.forEach((value, id) => {
        meta[id] = value;
      });
      window.localStorage.setItem(
        this.cacheKey(),
        JSON.stringify({ savedAt: Date.now(), ids: this.ids, meta })
      );
    } catch (error) {
      // Storage can be unavailable in private browsing.
    }
  }
}

function tidyDescription(text) {
  if (!text) return '';
  return text
    .split('\n')
    .map((line) => line.replace(/\s*[.…]+more\s*$/i, '').trim())
    .filter((line) => line && !/^[.…\s]*more$/i.test(line))
    .join('\n')
    .trim();
}

function extractDescription(markdown) {
  const marker = 'Markdown Content:';
  const body = markdown.includes(marker) ? markdown.slice(markdown.indexOf(marker) + marker.length) : markdown;
  const expanded = body.split(/Show less/i)[1];
  let source = expanded || body.split(/…more|\.\.\.more/i)[0] || '';
  source = source.split(/How this was made|\nTranscript|Show transcript|\n\[!\[/i)[0];
  const lines = source
    .replace(/\[[^\]]*]\([^)]*\)/g, '')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').replace(/\s*[.…]+more\s*$/i, '').trim())
    .filter((line) => line && !/^[.…\s]*more$/i.test(line) && !/^[\d,.\s]*views?\b/i.test(line));
  return tidyDescription(lines.join('\n'));
}

function uploadsPlaylistId(channelUrl) {
  const match = String(channelUrl).match(/\/channel\/(UC[\w-]{10,})/);
  if (!match) return '';
  return `UU${match[1].slice(2)}`;
}

function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (window.__ariYtApi) return window.__ariYtApi;

  window.__ariYtApi = new Promise((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === 'function') previous();
      resolve();
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.onerror = () => reject(new Error('api'));
    document.head.appendChild(script);
  });

  return window.__ariYtApi;
}

async function mapPool(items, limit, fn) {
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      await fn(items[index], index);
    }
  });
  await Promise.all(workers);
}

if (!customElements.get('ari-youtube-channel')) {
  customElements.define('ari-youtube-channel', AriYoutubeChannel);
}
